import { Link } from 'react-router';
import { BookOpen, CheckSquare, FileSignature, FileText, Grid3x3, History, KeyRound, Link2, Mail, Megaphone, Plane, Send, Settings, Target, Timer, Users, Video } from 'lucide-react';
import { useAuth } from '../auth/AuthContext.tsx';
import { Card } from '../components/Card.tsx';

export function AdminLandingPage(): React.ReactElement {
  const { agent } = useAuth();
  if (!agent) return <p>Loading…</p>;
  if (agent.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[var(--color-header)]">Admin</h1>
        <p className="mt-1 text-[var(--color-muted)]">Settings and integrations available only to admins.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Link to="/admin/agents" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <Users className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">Technicians</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  Add, edit, disable agent accounts. Set full-time / part-time. Issue or re-issue
                  enrollment links.
                </p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/admin/skills-matrix" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <Grid3x3 className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">Skills matrix</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  Fill in everyone's skill ratings (Prime / Second / Learn) in one grid. Add new
                  skills as the team grows. Read-only directory lives at /team.
                </p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/admin/pto" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <Plane className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">PTO</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  Approve or deny time-off requests, see everyone's earned / taken / available
                  days, book leave agreed off-system, and adjust a balance by hand.
                </p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/admin/goals" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <Target className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">Goals & Bonuses</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  Set bonus $ per goal per period, preview where every tech stands, close periods to
                  snapshot results for payroll, and export the CSV.
                </p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/admin/google" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <Mail className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">Team Google integration</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  The org's ticketing inbox. Inbound email, outbound email, and team-calendar
                  events all live on this Google account. Distinct from each agent's own Google
                  calendar (set up under Profile → My Google calendar).
                </p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/admin/microsoft" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <Video className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">Team Microsoft / Teams</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  The org's Teams meeting account. Every team-calendar event and check-in is
                  organized under this account. Distinct from each agent's own Teams license
                  (set up under Profile → My Teams license).
                </p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/admin/quotes" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <FileText className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">Quotes</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  Write the scope, price it, send it to the client and capture their signature.
                  Accepted quotes become contracts and then projects.
                </p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/admin/contracts" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <FileSignature className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">Contracts</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  Everything out with clients and everything signed, pulled from Qwilr. Triage
                  what each one is, link it to a client, and create the project when the work is
                  ready to start. Also open to billing admins.
                </p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/admin/settings" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <Settings className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">App settings</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  Ticketing email address shown to portal users. Change without redeploy.
                </p>
              </div>
            </div>
          </Card>
        </Link>
        {/* TEMPORARY — Zendesk cutover tools (AdminZendeskImportPage, AdminZendeskTicketImportPage,
            AdminTicketCsvOverridePage, AdminBulkImportPage). Routes still live in App.tsx; remove
            cards + pages + routes together once cutover is complete. */}
        <Link to="/admin/kb-categories" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <BookOpen className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">KB categories</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  Two-level categories that drive the portal landing tiles. Top-level categories
                  appear on the home page; subcategories appear on the parent's page.
                </p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/admin/subtask-templates" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <CheckSquare className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">Subtask templates</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  Reusable checklists with optional staggered due dates. Agents apply them on a
                  project to seed its subtasks.
                </p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/admin/hudu" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <Link2 className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">Hudu integration</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  Link each T3 organization to its Hudu company so org pages deep-link into Hudu.
                  Auto-match by name with manual review.
                </p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/admin/api-keys" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <KeyRound className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">API keys</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  Bearer tokens for the public read-only API. Issue one per integration; revoke
                  individually if leaked. Spec at /api/v1/docs.
                </p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/admin/portal-invites" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <Send className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">Send portal invites</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  One-shot bulk send to every portal-flagged contact who hasn't enrolled yet.
                  Each link is valid for 7 days. Re-running is safe.
                </p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/admin/broadcasts" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <Megaphone className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">Broadcasts</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  Email all agents and/or client contacts with a process update or announcement.
                  Posts a dismissible in-app banner alongside the email.
                </p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/timesheets" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <Timer className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">Running timers</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  On the Timesheets page: every timer running right now, across the team. Log a
                  clock someone left running — with the hours they actually worked — or discard it.
                  Also open to time admins.
                </p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/admin/audit-log" className="block">
          <Card className="h-full transition hover:border-[var(--color-t3-blue)]">
            <div className="flex items-start gap-3">
              <History className="mt-1 h-5 w-5 text-[var(--color-t3-blue)]" />
              <div>
                <h2 className="text-base font-semibold text-[var(--color-header)]">Audit log</h2>
                <p className="mt-1 text-sm text-[var(--color-muted)]">
                  Append-only record of security-relevant actions: SMTP test, role changes, etc.
                </p>
              </div>
            </div>
          </Card>
        </Link>
      </div>
    </div>
  );
}
